import rest from 'redux/rest'
import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, links, localStorage, object } from 'common/helpers'
import { modifyCart, ModifiedCartResult } from 'common/helpers/modifiers/modifyResponse'
import { getCart, modifyCartItems, removeCartItems, clearCart } from 'actions/cart'
import { InitialSubscriptionPlan } from 'typings/graphql'
import type { ModifiedCartData } from 'actions/cart'


export type CartResult = ModifiedCartResult

// @ts-ignore
const modifyCartDataResponse = ({ recommendationTag, ...rest } = {}) => {
  if (recommendationTag) {
    actions.models.recommendationTag.setTag(recommendationTag)
  }

  // @ts-ignore
  return modifyCart(rest)
}

const _updateItem = (id: number, { items, ...cartState }) => {
  updateCartState({ isFetching: false, ...cartState })

  const product = items.products.items[id]
  reducers.models.cart.updateProduct({ id, product })
}


const open = () => reducers.models.cart.setVisibility(true)

const close = () => {
  reducers.models.cart.setVisibility(false)
  // TODO looks weird - why do we try to open modal if we just close cart overlay, we try open it on changes in updateCartState()
  tryOpenRecommendationTagModal()
}

const tryOpenRecommendationTagModal = () => {
  const { models: { recommendationTag: { tag }, cart: { isVisible } } } = getState()

  if (tag && !isVisible) {
    actions.models.recommendationTag.fetch({ placement: 'cart' })
  }
}

const updateCartState = (result) => {
  // TODO looks weird - we call this before state update
  tryOpenRecommendationTagModal()
  reducers.models.cart.update(result)
}


// Fetch ----------------------------------------------------------------------------------------

const _recurlyFetch = (): Promise<ModifiedCartData> => {
  updateCartState({ isFetching: true })

  return getCart()
    .then(({ data, error }) => {
      if (error) {
        console.error(error.code, error.message)
        updateCartState({ isFetching: false })

        return Promise.reject(error)
      }

      updateCartState({ ...data, isFetching: false })

      return data
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const _vindiciaFetch = (): Promise<ModifiedCartResult> => {
  updateCartState({ isFetching: true })

  return rest.models.cart.fetch({
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExist } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExist })
      updateCartState({ ...result, isFetching: false })

      return result
    }, (err) => {
      updateCartState({ isFetching: false })
      return Promise.reject(err)
    })
}

const fetch = (): Promise<ModifiedCartResult> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _recurlyFetch()
  }

  return _vindiciaFetch()
}


// Subscription ------------------------------------------------------------------------------

const _addRecurlySubscription = (withLoader: boolean) => {
  // TODO remove global loader - added on 5/14/20 by pavelivanov
  if (withLoader) {
    actions.models.ui.showRequestLoader()
  }

  return modifyCartItems({
    subscriptionItem: {
      plan: InitialSubscriptionPlan.MONTHLY,
    },
  })
    .finally(() => {
      if (withLoader) {
        actions.models.ui.hideRequestLoader()
      }
    })
    .then(({ data, error }) => {
      if (error) {
        console.error(error.code, error.message)
        updateCartState({ isFetching: false })

        return Promise.reject(error)
      }

      updateCartState({ ...data, isFetching: false })
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const addSubscription = (withLoader = true) => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _addRecurlySubscription(withLoader)
  }

  if (withLoader) {
    actions.models.ui.showRequestLoader()
  }

  return request.put('rest/cart/subscription')
    .then(async (result) => {
      try {
        // ATTN we need to wait cart fetching, because payment depends on cart state
        await fetch()
      }
      catch (error) {
        console.error(error)
      }

      return result
    })
    .finally(() => withLoader ? actions.models.ui.hideRequestLoader() : null)
}

const _removeRecurlySubscription = () =>
  removeCartItems({
    subscriptionItem: {
      // TODO looks weird that we pass PLAN - added on 5/14/20 by pavelivanov
      plan: InitialSubscriptionPlan.MONTHLY,
    },
  })

const removeSubscription = () => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _removeRecurlySubscription()
  }

  return request.delete(`rest/cart/subscription`)
    .then(async (result) => {
      try {
        // ATTN we need to wait cart fetching, because payment depends on cart state
        await fetch()
      }
      catch (error) {
        console.error(error)
      }

      return result
    })
}


// Add Item ----------------------------------------------------------------------------------

type Item = {
  tradingId: number
  id: number
  brand: string
  name: string
  amount: number
  price: number
}

type Metadata = {
  placement: string
}

const _addRecurlyItem = (productLineItemId: number, count: number = 1, metadata?: Metadata) => {
  const state = getState()
  const currQuantity = object.get(state, `models.cart.items.products.items.${productLineItemId}.quantity`, 0)
  const quantity = currQuantity + count

  updateCartState({ isFetching: true })

  return modifyCartItems({
    productItems: [
      {
        productLineItemId,
        quantity,
      },
    ],
  })
    .then(({ data, error }) => {
      if (error) {
        console.error(error.code, error.message)
        updateCartState({ isFetching: false })

        return Promise.reject(error)
      }

      const updatedProduct = object.get(data, `items.products.items.${productLineItemId}`)
      const { models: { cart: { isVisible: isCartSidebarVisible } } } = getState()

      updateCartState({ ...data, isFetching: false })

      if (
        !isCartSidebarVisible
        && updatedProduct
        && metadata
        && metadata.placement !== 'toss-in page' // TODO write description why we don't show notification for TOSSIN - added on 5/13/20 by pavelivanov
      ) {
        actions.misc.notifications.show('perfumeItem', {
          item: updatedProduct,
          placement: metadata.placement,
          onClose: () => open(),
        })
      }

      return data
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const _addVindiciaItem = (item: Item, count: number = 1, metadata: Metadata, withNotification: boolean = true) => {
  const { id, tradingId } = item

  reducers.models.cart.setFetching(true)

  return request.post('rest/cart', {
    body: {
      item: tradingId || id,
      count,
      metadata,
    },
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { models: { cart: { isVisible } } } = getState()

      if (!isVisible && metadata && metadata.placement !== 'toss-in page') {
        if (withNotification) {
          actions.misc.notifications.show('perfumeItem', {
            item,
            placement: metadata.placement,
            onClose: () => open(),
          })
        }
      }

      const { isFlashSaleRemoved, isFlashSaleExist } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExist })
      updateCartState(result)

      return result
    })
    .finally(() => reducers.models.cart.setFetching(false))
}

// Save and add product after registration / authorization
const _addItemPostponed = (item, metadata, withNotification) => {
  const cartProduct = { item: { ...item, amount: 1 }, metadata, withNotification }

  localStorage.setItem(constants.localStorageNames.cartProduct, cartProduct)
  // window.location.href for reloading page if redirect from SEO page
  actions.models.router.push(links.register, true, true)

  return Promise.resolve()
}

const addItem = (item: Item, metadata: Metadata, withNotification: boolean = true) => {
  const { models: { me: { paymentProvider, isLoggedIn } } } = getState()

  if (isLoggedIn) {
    if (paymentProvider === 'RECURLY') {
      return _addRecurlyItem(item.tradingId || item.id, 1, metadata)
    }

    return _addVindiciaItem(item, item.amount || 1, metadata, withNotification)
  }

  return _addItemPostponed(item, metadata, withNotification)
}


// Increase -----------------------------------------------------------------------------------

const _increaseRecurlyItemQuantity = (productLineItemId: number) => {
  const state = getState()
  const currQuantity = object.get(state, `models.cart.items.products.items.${productLineItemId}.quantity`, 0)
  const quantity = currQuantity + 1

  updateCartState({ isFetching: true })

  return modifyCartItems({
    productItems: [
      {
        productLineItemId,
        quantity,
      },
    ],
  })
    .then(({ data, error }) => {
      if (error) {
        console.error(error.code, error.message)
        updateCartState({ isFetching: false })

        return Promise.reject(error)
      }

      updateCartState({ ...data, isFetching: false })

      return data
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const increaseItemCount = (id: number) => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _increaseRecurlyItemQuantity(id)
  }

  updateCartState({ isFetching: true })

  return request.post(`rest/cart/items/${id}`, {
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExist } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExist })
      _updateItem(id, result)

      return result
    })
}


// Decrease -------------------------------------------------------------------------------------

const _decreaseRecurlyItemQuantity = (productLineItemId: number) => {
  const state = getState()
  const currQuantity = object.get(state, `models.cart.items.products.items.${productLineItemId}.quantity`, 0)
  const quantity = currQuantity - 1

  updateCartState({ isFetching: true })

  return modifyCartItems({
    productItems: [
      {
        productLineItemId,
        quantity,
      },
    ],
  })
    .then(({ data, error }) => {
      if (error) {
        console.error(error.code, error.message)
        updateCartState({ isFetching: false })

        return Promise.reject(error)
      }

      updateCartState({ ...data, isFetching: false })

      return data
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const decreaseItemCount = (id: number) => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    const state = getState()
    const currQuantity = object.get(state, `models.cart.items.products.items.${id}.quantity`, 0)

    // item will be removed from cart
    if (currQuantity === 1) {
      return _removeRecurlyItem(id)
    }

    return _decreaseRecurlyItemQuantity(id)
  }

  updateCartState({ isFetching: true })

  return request.delete(`rest/cart/items/${id}`, {
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExist } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExist })
      _updateItem(id, result)

      return result
    })
}


// Remove Item ----------------------------------------------------------------------------------------

const _removeRecurlyItem = (productLineItemId: number) => {
  updateCartState({ isFetching: true })

  return removeCartItems({
    productItems: [
      {
        productLineItemId,
      },
    ],
  })
    .then(({ data, error }) => {
      if (error) {
        console.error(error.code, error.message)
        updateCartState({ isFetching: false })

        return Promise.reject(error)
      }

      updateCartState({ ...data, isFetching: false })
      // TODO add flashsale? - added on 5/13/20 by pavelivanov

      return data
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const removeProduct = (id: number) => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _removeRecurlyItem(id)
  }

  reducers.models.cart.setFetching(true)

  return request.delete(`rest/cart/items/${id}?all=true`, {
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExist } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExist })
      _updateItem(id, result)

      return result
    })
    .finally(() => reducers.models.cart.setFetching(false))
}


// ---------------------------------------------------------------------------------------------------

const _recurlyClear = () => {
  updateCartState({ isFetching: true })

  return clearCart()
    .then(() => {
      reducers.models.cart.clear()
    })
    .catch(() => {
      updateCartState({ isFetching: false })
      return Promise.reject() // TODO return error - added on 5/13/20 by pavelivanov
    })
}

const clear = () => {
  reducers.models.cart.clear()
}


// ---------------------------------------------------------------------------------------------------

const fetchTotalPrice = () =>
  rest.models.cart.fetchTotalPrice()
    .then(({ price }) => {
      reducers.models.cart.setTotalPrice(price)
      return price
    })

type PostponedProduct = {
  item: Item
  metadata: Metadata
  withNotification: boolean
}

const resolvePostponedItem = () => {
  const { models: { me: { paymentProvider } } } = getState()
  const cartProduct = localStorage.getItem(constants.localStorageNames.cartProduct, {}) as PostponedProduct

  localStorage.removeItem(constants.localStorageNames.cartProduct)

  if (!cartProduct || !cartProduct.item) {
    return Promise.resolve()
  }

  const { item, metadata, withNotification } = cartProduct

  if (paymentProvider === 'RECURLY') {
    return _addRecurlyItem(item.id, item.amount, metadata)
  }

  return _addVindiciaItem(item, item.amount || 1, metadata, withNotification)
}


export default {
  open,
  close,
  fetch,
  fetchTotalPrice,
  addItem,
  resolvePostponedItem,
  increaseItemCount,
  decreaseItemCount,
  removeProduct,
  addSubscription,
  removeSubscription,
  clear,
}
